// What people sent through the report's forms (design §5, §7): who, when,
// for which version, whether an agent has read it yet, and the data — shown
// as text, never as HTML.

import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { formatDateTime, formatRelative, shortId } from '../../lib/format'
import { ReportDataView } from './report-data-view'
import type { ReportSubmissionView } from './report-types'

export interface ReportSubmissionsProps {
  submissions: ReportSubmissionView[]
  /** How many to show before "Show older". */
  initialCount?: number
}

export function ReportSubmissions({
  submissions,
  initialCount = 5,
}: ReportSubmissionsProps) {
  const [showAll, setShowAll] = useState(false)
  const sorted = [...submissions].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  )
  if (sorted.length === 0) {
    return (
      <p className="text-sm text-ink-3">
        Nothing sent yet. When the report has a form, what people send shows
        up here.
      </p>
    )
  }
  const visible = showAll ? sorted : sorted.slice(0, initialCount)

  return (
    <div className="space-y-3">
      <ol className="divide-y divide-line rounded-panel border border-line">
        {visible.map((submission) => (
          <li key={submission.id} className="px-4 py-3">
            <p className="flex flex-wrap items-center gap-x-2 text-sm text-ink-3">
              <span className="font-medium text-ink">
                {submission.submittedByUserName ??
                  shortId(submission.submittedByUserId)}
              </span>
              <span>·</span>
              <span title={formatDateTime(submission.createdAt)}>
                {formatRelative(submission.createdAt)}
              </span>
              <span>·</span>
              <span>version {submission.version}</span>
              {submission.seenByRunId ? (
                <span className="rounded-full bg-moss-soft px-2 text-xs font-medium text-moss">
                  read by{' '}
                  <Link
                    to="/runs/$runId"
                    params={{ runId: submission.seenByRunId }}
                    className="underline-offset-2 hover:underline"
                  >
                    run {shortId(submission.seenByRunId)}
                  </Link>
                </span>
              ) : (
                <span className="rounded-full bg-sunken px-2 text-xs font-medium text-ink-3">
                  not read by an agent yet
                </span>
              )}
            </p>
            <div className="mt-2">
              <ReportDataView data={submission.data} />
            </div>
          </li>
        ))}
      </ol>
      {sorted.length > visible.length ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => setShowAll(true)}
        >
          Show {sorted.length - visible.length} older
        </button>
      ) : null}
    </div>
  )
}
